/**
 * Upload dialog for shared files. Extracted from SharedFilesView so babel
 * has less JSX to traverse in a single module.
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Lock, Upload, Users, X, MousePointerClick } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { api, formatApiErrorDetail } from "../../lib/api";
import { sharedFilesApi } from "../../lib/copApi";
import { formatSize } from "./sharedFilesHelpers";

// UX constraints — enforced client-side. Backend still caps every single
// file at 500 MB via MAX_FILE_BYTES.
const MAX_BATCH_FILES = 10;
const MAX_BATCH_BYTES = 500 * 1024 * 1024;

export default function SharedFileUploadDialog({
  open,
  onOpenChange,
  folderId,
  onUploaded,
  filesApi = sharedFilesApi,
}) {
  const [files, setFiles] = useState([]);
  const [progress, setProgress] = useState({ done: 0, total: 0, currentName: "" });
  const [isDragging, setIsDragging] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [recipientAll, setRecipientAll] = useState(false);
  const [recipientIds, setRecipientIds] = useState(new Set());
  const [users, setUsers] = useState([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setFiles([]);
    setPassword("");
    setShowPassword(false);
    setRecipientAll(false);
    setRecipientIds(new Set());
    setProgress({ done: 0, total: 0, currentName: "" });
    if (inputRef.current) inputRef.current.value = "";
    api
      .get("/users")
      .then((r) => setUsers(r.data || []))
      .catch(() => setUsers([]));
  }, [open]);

  const totalBytes = files.reduce((s, f) => s + f.size, 0);
  const hasPw = password.trim().length > 0;

  const handleFileChange = (list) => {
    const arr = Array.from(list || []);
    if (arr.length === 0) return;
    // Enforce 10-file batch cap.
    if (arr.length > MAX_BATCH_FILES) {
      toast.error(`Aynı anda en fazla ${MAX_BATCH_FILES} dosya seçebilirsin`);
      return;
    }
    // Enforce 500 MB total batch cap.
    const total = arr.reduce((s, f) => s + f.size, 0);
    if (total > MAX_BATCH_BYTES) {
      toast.error("Toplam boyut 500 MB'ı aşamaz");
      return;
    }
    setFiles(arr);
  };

  const removeAt = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
    if (inputRef.current) inputRef.current.value = "";
  };

  const toggleUser = (id) => {
    setRecipientIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const submit = async () => {
    if (files.length === 0) return;
    if (hasPw && !recipientAll && recipientIds.size === 0) {
      toast.error(
        "Şifreli dosya için en az bir alıcı seç veya 'Herkes'i işaretle",
      );
      return;
    }
    setUploading(true);
    setProgress({ done: 0, total: files.length, currentName: files[0].name });
    let failed = 0;
    // Sequential upload — keeps notification ordering stable and avoids
    // hammering the ingress with 10 parallel multi-hundred-MB streams.
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      setProgress({ done: i, total: files.length, currentName: f.name });
      try {
        await filesApi.upload(f, {
          folder_id: folderId,
          password: password || undefined,
          recipient_ids: hasPw ? Array.from(recipientIds) : undefined,
          recipient_all: hasPw && recipientAll,
        });
      } catch (err) {
        failed += 1;
        toast.error(
          `"${f.name}": ${
            formatApiErrorDetail(err.response?.data?.detail) || err.message
          }`,
        );
      }
    }
    setUploading(false);
    setProgress({ done: files.length, total: files.length, currentName: "" });
    const ok = files.length - failed;
    if (ok > 0) {
      toast.success(
        ok === 1
          ? `"${files[0].name}" yüklendi`
          : `${ok} dosya yüklendi${failed ? ` (${failed} başarısız)` : ""}`,
      );
    }
    onOpenChange(false);
    onUploaded?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] max-w-lg overflow-y-auto"
        data-testid="shared-file-upload-dialog"
      >
        <DialogHeader>
          <DialogTitle>Dosya yükle</DialogTitle>
          <DialogDescription>
            Aynı anda en fazla {MAX_BATCH_FILES} dosya, toplam 500 MB.
            {filesApi.supportsPassword
              ? ' Şifre girersen tüm dosyalar aynı şifreyle korunur ve seçilen kişilere DM + e-posta ile "Şifreyi görüntüle" linki gönderilir.'
              : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Dosyalar</Label>
            {/* Drop zone — accepts drag-and-drop from OS file manager
                as well as classic click-to-browse via the hidden input. */}
            <div
              onDragEnter={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!uploading) setIsDragging(true);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!uploading) setIsDragging(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.stopPropagation();
                // Only clear when leaving the dropzone itself, not a child.
                if (e.currentTarget.contains(e.relatedTarget)) return;
                setIsDragging(false);
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDragging(false);
                if (uploading) return;
                if (e.dataTransfer?.files?.length)
                  handleFileChange(e.dataTransfer.files);
              }}
              onClick={() => inputRef.current?.click()}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ")
                  inputRef.current?.click();
              }}
              data-testid="shared-upload-dropzone"
              className={
                isDragging
                  ? "flex cursor-copy flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-violet-500 bg-violet-50/70 p-6 text-center transition-colors"
                  : "flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-zinc-300 bg-zinc-50 p-6 text-center transition-colors hover:border-zinc-400 hover:bg-zinc-100"
              }
            >
              <Upload
                className={
                  isDragging
                    ? "h-6 w-6 text-violet-600"
                    : "h-6 w-6 text-zinc-400"
                }
              />
              <div className="text-sm font-medium text-zinc-700">
                {isDragging
                  ? "Bırak, hemen yüklüyoruz…"
                  : "Dosyaları buraya sürükle veya seçmek için tıkla"}
              </div>
              <div className="flex items-center gap-1 text-[11px] text-zinc-500">
                <MousePointerClick className="h-3 w-3" />
                Max {MAX_BATCH_FILES} dosya · toplam 500 MB
              </div>
              <input
                ref={inputRef}
                type="file"
                multiple
                onChange={(e) => handleFileChange(e.target.files)}
                data-testid="shared-upload-input"
                className="hidden"
              />
            </div>
            {files.length > 0 && (
              <div className="rounded-md border border-zinc-200 bg-zinc-50">
                {files.map((f, i) => (
                  <div
                    key={`${f.name}-${i}`}
                    className="flex items-center justify-between gap-2 border-b border-zinc-200 px-2.5 py-1.5 text-xs last:border-b-0"
                    data-testid={`shared-upload-file-row-${i}`}
                  >
                    <div className="min-w-0 flex-1 truncate">
                      <span className="font-medium text-zinc-800">
                        {f.name}
                      </span>
                      <span className="ml-1 text-zinc-500">
                        · {formatSize(f.size)}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeAt(i);
                      }}
                      className="rounded p-0.5 text-zinc-400 hover:bg-red-100 hover:text-red-600"
                      title="Kaldır"
                      data-testid={`shared-upload-remove-${i}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                <div className="flex items-center justify-between border-t border-zinc-200 bg-white px-2.5 py-1.5 text-[11px] text-zinc-600">
                  <span>
                    {files.length}/{MAX_BATCH_FILES} dosya
                  </span>
                  <span className={totalBytes > MAX_BATCH_BYTES ? "font-semibold text-red-600" : ""}>
                    {formatSize(totalBytes)} / 500 MB
                  </span>
                </div>
              </div>
            )}
          </div>

          {filesApi.supportsPassword && (
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5">
                <Lock className="h-3.5 w-3.5 text-amber-600" />
                Şifre (opsiyonel)
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Boş bırak: şifresiz"
                  data-testid="shared-upload-password-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPassword((v) => !v)}
                >
                  {showPassword ? "Gizle" : "Göster"}
                </Button>
              </div>
            </div>
          )}

          {filesApi.supportsPassword && hasPw && (
            <RecipientPicker
              recipientAll={recipientAll}
              setRecipientAll={setRecipientAll}
              recipientIds={recipientIds}
              toggleUser={toggleUser}
              users={users}
            />
          )}
        </div>

        <DialogFooter>
          {uploading && progress.total > 0 && (
            <div
              className="mr-auto flex items-center gap-2 text-[11px] text-zinc-500"
              data-testid="shared-upload-progress"
            >
              <Loader2 className="h-3 w-3 animate-spin" />
              {progress.done}/{progress.total} · {progress.currentName}
            </div>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={uploading}>
            Vazgeç
          </Button>
          <Button
            onClick={submit}
            disabled={files.length === 0 || uploading || totalBytes > MAX_BATCH_BYTES}
            data-testid="shared-upload-submit-btn"
          >
            {uploading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )}
            {files.length > 1 ? `${files.length} dosyayı yükle` : "Yükle"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RecipientPicker({
  recipientAll,
  setRecipientAll,
  recipientIds,
  toggleUser,
  users,
}) {
  return (
    <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50/40 p-3">
      <div className="flex items-center gap-2 text-xs font-semibold text-amber-900">
        <Users className="h-3.5 w-3.5" />
        Kimlere gönderilsin?
      </div>
      <label className="flex items-center gap-2 text-sm">
        <Checkbox
          checked={recipientAll}
          onCheckedChange={(v) => setRecipientAll(!!v)}
          data-testid="recipient-all-checkbox"
        />
        Tüm tenant kullanıcıları (kendin hariç)
      </label>
      {!recipientAll && (
        <div className="max-h-40 overflow-y-auto rounded-md border border-zinc-200 bg-white">
          {users.length === 0 ? (
            <div className="p-3 text-xs text-zinc-500">Kullanıcı yok.</div>
          ) : (
            users.map((u) => (
              <label
                key={u.id}
                className="flex items-center gap-2 border-b border-zinc-100 px-2.5 py-1.5 text-sm last:border-b-0 hover:bg-zinc-50"
              >
                <Checkbox
                  checked={recipientIds.has(u.id)}
                  onCheckedChange={() => toggleUser(u.id)}
                  data-testid={`recipient-user-${u.id}`}
                />
                <span className="truncate">
                  {u.name}{" "}
                  <span className="text-[11px] text-zinc-400">
                    ({u.email})
                  </span>
                </span>
              </label>
            ))
          )}
        </div>
      )}
    </div>
  );
}
